import { ArtistProfile, DspName, FormattedDspName, V2ProUser } from 'types';
import { WizardContext } from 'components/Wizard/types';
import { ARTIST_WIZARD_CONTEXTS } from 'components/Wizard/constants';
import { isArtistSoundCloudBypassEnabled } from 'helpers/artists';

// Currently, only SoundCloud DSP's have a bypass feature
const isDspBypassAllowed = (service: DspName) => service === 'soundcloud';
const isDspBypassEnabledForArtist = (artistProfile: ArtistProfile) =>
  isArtistSoundCloudBypassEnabled(artistProfile);

/**
 * [canUserBypassDsp] - determine if the selection for bypassing DSP mapping can be shown. Currently this is an
 *  admin set configuration that will only be displayed if the config is set.
 */

export const canUserBypassDsp = (service: DspName, artistProfile: ArtistProfile) => {
  const isAllowed = isDspBypassAllowed(service);
  const isEnabled = isDspBypassEnabledForArtist(artistProfile);

  return isAllowed && isEnabled;
};

/**
 * [canUserCreateNewDspProfile] - determine if the selection for creating a new DSP profile
 * should be displayed
 *
 * User will see the 'New DSP Profile' card if:
 * - Service is Spotify or Apple... see code for Apple exceptions
 */

export const canUserCreateNewDspProfile = (
  artistProfile: ArtistProfile,
  service: DspName,
  context: WizardContext
) => {
  if (context !== ARTIST_WIZARD_CONTEXTS.LABEL_RELEASE) return false;

  const artistConnection = artistProfile.connections.find(({ dsp }) => dsp === service);
  if (artistConnection) return false;

  if (!['spotify', 'apple_music', 'apple-music'].includes(service)) return false;

  return true;
};

/**
 * [canUserSeeSupportCard] - determine if the instructions for contacting support should be displayed
 *
 * User will see the support card if:
 * - bypass is allowed but not enabled
 * - Apple dsp page and artist profile cannot be created
 */

export const canUserSeeSupportCard = (
  artistProfile: ArtistProfile,
  canCreateDspProfile: boolean,
  service: DspName
) => {
  if (['apple_music', 'apple-music'].includes(service) && !canCreateDspProfile)
    return true;

  const isBypassAllowed = isDspBypassAllowed(service);
  const isBypassEnabled = isDspBypassEnabledForArtist(artistProfile);
  if (isBypassAllowed && !isBypassEnabled) return true;

  return false;
};

/**
 * [getMissingDspEmailHref]
 *
 * Generates a mailto href for users who cannot find a DSP profile for provided artist
 */

export const getMissingDspEmailHref = (
  artistProfile: ArtistProfile,
  serviceName: FormattedDspName,
  user: V2ProUser
) => {
  const mailTo = 'mailto:support@protonradio.com';
  const subject = encodeURI(
    `Cannot find ${serviceName} profile for ${artistProfile.name}`
  );
  const body = encodeURI(
    `

    ##- Please type your message (optional) above this line -##

    A ${serviceName} profile could not be found for '${artistProfile.name}' while using the artist profile editor.

    URL: ${window.location.href}
    User: ${user.first_name} ${user.last_name}
    User id: ${user.user_id}`
  );

  return `${mailTo}?subject=${subject}&body=${body}`;
};

/**
 * [baseCharacterMatch]
 *
 * Compares two strings by base characters for case/accent-insensitive match
 */

export const baseCharacterMatch = (a: string, b: string): boolean => {
  return Intl.Collator('en', { sensitivity: 'base' }).compare(a, b) === 0;
};
